import { useState, useEffect } from 'react'
import { MessageSquare, CheckCircle, XCircle, TrendingUp, RefreshCw, Search, Filter, Download } from 'lucide-react'

interface PromptLog {
  id: string
  timestamp: string
  tenantId: string
  userEmail: string
  prompt: string
  answered: boolean
  responseLength?: number
  toolsUsed?: string[]
  error?: string
}

interface PromptAnalytics {
  total: number
  answered: number
  failed: number
  successRate: number
  avgResponseLength: number
  topTools: { name: string; count: number }[]
  commonFailures: { prompt: string; count: number }[]
  recentPrompts: PromptLog[]
}

const AIPromptAnalytics = () => {
  const [analytics, setAnalytics] = useState<PromptAnalytics | null>(null)
  const [prompts, setPrompts] = useState<PromptLog[]>([])
  const [filteredPrompts, setFilteredPrompts] = useState<PromptLog[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Filters
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<'all' | 'answered' | 'failed'>('all')
  const [tenantFilter, setTenantFilter] = useState('')
  const [dateRange, setDateRange] = useState<'24h' | '7d' | '30d' | 'all'>('7d')

  useEffect(() => {
    loadAnalytics()
  }, [])

  useEffect(() => {
    applyFilters()
  }, [prompts, searchQuery, statusFilter, tenantFilter, dateRange])

  const loadAnalytics = async () => {
    setLoading(true)
    setError(null)

    try {
      const response = await fetch('/api/platform-admin/ai-prompts', {
        credentials: 'include'
      })

      if (!response.ok) {
        throw new Error('Failed to load AI prompt analytics')
      }

      const data = await response.json()
      setAnalytics(data.analytics)
      setPrompts(data.prompts)
    } catch (err) {
      console.error('Analytics error:', err)
      setError(err instanceof Error ? err.message : 'Failed to load analytics')
    } finally {
      setLoading(false)
    }
  }

  const applyFilters = () => {
    let filtered = [...prompts]

    // Date range filter
    const now = new Date()
    const cutoff = new Date()
    switch (dateRange) {
      case '24h':
        cutoff.setHours(now.getHours() - 24)
        break
      case '7d':
        cutoff.setDate(now.getDate() - 7)
        break
      case '30d':
        cutoff.setDate(now.getDate() - 30)
        break
      case 'all':
        cutoff.setFullYear(1970)
        break
    }
    filtered = filtered.filter(p => new Date(p.timestamp) >= cutoff)

    // Status filter
    if (statusFilter === 'answered') {
      filtered = filtered.filter(p => p.answered)
    } else if (statusFilter === 'failed') {
      filtered = filtered.filter(p => !p.answered)
    }

    // Tenant filter
    if (tenantFilter) {
      filtered = filtered.filter(p => p.tenantId.toLowerCase().includes(tenantFilter.toLowerCase()))
    }

    // Search query
    if (searchQuery) {
      filtered = filtered.filter(p =>
        p.prompt.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.userEmail.toLowerCase().includes(searchQuery.toLowerCase())
      )
    }

    setFilteredPrompts(filtered)
  }

  const downloadCSV = () => {
    const headers = ['Timestamp', 'Tenant ID', 'User Email', 'Prompt', 'Answered', 'Response Length', 'Tools Used', 'Error']
    const rows = filteredPrompts.map(p => [
      p.timestamp,
      p.tenantId,
      p.userEmail,
      `"${p.prompt.replace(/"/g, '""')}"`,
      p.answered ? 'Yes' : 'No',
      p.responseLength || '',
      p.toolsUsed?.join('; ') || '',
      p.error || ''
    ])

    const csv = [headers, ...rows].map(row => row.join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `ai-prompt-analytics-${new Date().toISOString()}.csv`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  if (loading && !analytics) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-3 text-gray-600">Loading AI prompt analytics...</span>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="flex items-center gap-3">
          <XCircle className="w-6 h-6 text-red-600" />
          <div>
            <h3 className="font-semibold text-red-900">Error Loading Analytics</h3>
            <p className="text-sm text-red-700 mt-1">{error}</p>
          </div>
        </div>
        <button
          onClick={loadAnalytics}
          className="mt-4 btn-primary text-sm"
        >
          Retry
        </button>
      </div>
    )
  }

  if (!analytics) {
    return null
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">AI Assistant Analytics</h2>
          <p className="text-sm text-gray-600 mt-1">
            Track user prompts and identify improvement opportunities
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={downloadCSV}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
          <button
            onClick={loadAnalytics}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-600">Total Prompts</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{analytics.total}</p>
              <p className="text-xs text-gray-500 mt-1">All time</p>
            </div>
            <div className="p-3 rounded-lg bg-blue-100">
              <MessageSquare className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-600">Success Rate</p>
              <p className="text-3xl font-bold text-green-600 mt-2">{analytics.successRate.toFixed(1)}%</p>
              <p className="text-xs text-gray-500 mt-1">{analytics.answered} answered</p>
            </div>
            <div className="p-3 rounded-lg bg-green-100">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-600">Failed Prompts</p>
              <p className="text-3xl font-bold text-red-600 mt-2">{analytics.failed}</p>
              <p className="text-xs text-gray-500 mt-1">Need attention</p>
            </div>
            <div className="p-3 rounded-lg bg-red-100">
              <XCircle className="w-6 h-6 text-red-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-600">Avg Response</p>
              <p className="text-3xl font-bold text-purple-600 mt-2">{analytics.avgResponseLength}</p>
              <p className="text-xs text-gray-500 mt-1">characters</p>
            </div>
            <div className="p-3 rounded-lg bg-purple-100">
              <TrendingUp className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Top Tools and Common Failures */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Top Tools */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Most Used Tools</h3>
          {analytics.topTools.length > 0 ? (
            <div className="space-y-3">
              {analytics.topTools.slice(0, 5).map((tool, index) => (
                <div key={index} className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">{tool.name}</span>
                  <div className="flex items-center gap-2">
                    <div className="w-32 bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full"
                        style={{ width: `${(tool.count / analytics.topTools[0].count) * 100}%` }}
                      />
                    </div>
                    <span className="text-sm font-semibold text-gray-900 w-8 text-right">{tool.count}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500">No tool usage data yet</p>
          )}
        </div>

        {/* Common Failures */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Common Failed Prompts</h3>
          {analytics.commonFailures.length > 0 ? (
            <div className="space-y-3">
              {analytics.commonFailures.slice(0, 5).map((failure, index) => (
                <div key={index} className="border-l-4 border-red-400 pl-3">
                  <p className="text-sm text-gray-700 line-clamp-2">{failure.prompt}</p>
                  <p className="text-xs text-red-600 mt-1">Failed {failure.count} time{failure.count > 1 ? 's' : ''}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500">No failures recorded</p>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center gap-3 mb-4">
          <Filter className="w-5 h-5 text-gray-600" />
          <h3 className="text-lg font-semibold text-gray-900">Filter Prompts</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search prompts..."
              className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as 'all' | 'answered' | 'failed')}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">All Status</option>
            <option value="answered">Answered Only</option>
            <option value="failed">Failed Only</option>
          </select>

          {/* Date Range */}
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value as '24h' | '7d' | '30d' | 'all')}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="24h">Last 24 Hours</option>
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
            <option value="all">All Time</option>
          </select>

          {/* Tenant Filter */}
          <input
            type="text"
            value={tenantFilter}
            onChange={(e) => setTenantFilter(e.target.value)}
            placeholder="Filter by tenant..."
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        <p className="text-sm text-gray-500 mt-3">
          Showing {filteredPrompts.length} of {prompts.length} prompts
        </p>
      </div>

      {/* Prompts List */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Recent Prompts</h3>
        </div>
        <div className="divide-y divide-gray-200">
          {filteredPrompts.length === 0 ? (
            <div className="p-8 text-center">
              <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600">No prompts match your filters</p>
            </div>
          ) : (
            filteredPrompts.slice(0, 50).map((prompt) => (
              <div key={prompt.id} className="p-4 hover:bg-gray-50">
                <div className="flex items-start gap-4">
                  {/* Status Icon */}
                  <div className="flex-shrink-0 mt-1">
                    {prompt.answered ? (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-600" />
                    )}
                  </div>

                  {/* Prompt Details */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">{prompt.prompt}</p>
                        <div className="flex items-center gap-3 mt-1">
                          <span className="text-xs text-gray-500">{prompt.userEmail}</span>
                          <span className="text-xs text-gray-400">•</span>
                          <span className="text-xs text-gray-500">{prompt.tenantId.slice(0, 8)}</span>
                          <span className="text-xs text-gray-400">•</span>
                          <span className="text-xs text-gray-500">
                            {new Date(prompt.timestamp).toLocaleString()}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Tools Used */}
                    {prompt.toolsUsed && prompt.toolsUsed.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {prompt.toolsUsed.map((tool, index) => (
                          <span
                            key={index}
                            className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700"
                          >
                            {tool}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Error Message */}
                    {!prompt.answered && prompt.error && (
                      <div className="mt-2 bg-red-50 border border-red-200 rounded p-2">
                        <p className="text-xs text-red-700">
                          <strong>Error:</strong> {prompt.error}
                        </p>
                      </div>
                    )}

                    {/* Response Length */}
                    {prompt.answered && prompt.responseLength && (
                      <p className="text-xs text-gray-500 mt-2">
                        Response: {prompt.responseLength} characters
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {filteredPrompts.length > 50 && (
          <div className="p-4 border-t border-gray-200 bg-gray-50 text-center">
            <p className="text-sm text-gray-600">
              Showing first 50 of {filteredPrompts.length} prompts. Use filters to narrow results.
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

export default AIPromptAnalytics
